/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * AppLayout - Application Layout
 * ============================================================================
 *
 * Main layout wrapper. Sidebar rendering is handled by module-specific layouts
 * (VentaLayout, SuperAdminLayout) via ModuleShell, so this component only
 * provides the Header and content area with proper margin offset.
 *
 * The CSS variable --module-shell-width is set by each shell component
 * to control the content area's left margin.
 *
 * ============================================================================
 */

import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { SupportSessionBanner } from './SupportSessionBanner';
import { useSessionMonitor } from '@/hooks/useSessionMonitor';
import { SessionTimeoutWarning } from '@/components/auth/SessionTimeoutWarning';
import { useAuthStore } from '@/stores/authStore';
import { UatWidget } from '@/components/uat/UatWidget';
import { UatBanner } from '@/components/uat/UatBanner';
import { useMyOrgSettings } from '@/hooks/api/useMyOrgSettings';

export function AppLayout() {
  const { user } = useAuthStore();
  const location = useLocation();
  const { data: orgSettings } = useMyOrgSettings();

  const uatEnabled = orgSettings?.settings.find(s => s.key === 'uat_enabled')?.effectiveValue === 'true';

  // Determine if this route has a module shell sidebar
  const hasSidebar =
    user?.role === 'SUPER_ADMIN' ||
    location.pathname.startsWith('/venta');

  const {
    showWarning,
    minutesRemaining,
    secondsRemaining,
    isTokenExpiry,
    isExtending,
    extendSession,
    dismissWarning,
    logoutNow,
  } = useSessionMonitor();

  return (
    <div className="app-layout">
      <div className={hasSidebar ? 'app-main app-main--with-sidebar' : 'app-main'}>
        <SupportSessionBanner />
        <Header />
        {uatEnabled && location.pathname.startsWith('/venta') && <UatBanner />}
        <main className={hasSidebar ? 'app-content app-content--module' : 'app-content'}>
          <Outlet />
        </main>
      </div>

      {uatEnabled && location.pathname.startsWith('/venta') && <UatWidget />}

      {/* Session Timeout Warning Modal */}
      <SessionTimeoutWarning
        isOpen={showWarning}
        minutesRemaining={minutesRemaining}
        secondsRemaining={secondsRemaining}
        isTokenExpiry={isTokenExpiry}
        isExtending={isExtending}
        onExtendSession={extendSession}
        onLogout={logoutNow}
        onDismiss={dismissWarning}
      />

      <style>{layoutStyles}</style>
    </div>
  );
}

const layoutStyles = `
  .app-layout {
    display: flex;
    min-height: 100vh;
    background: var(--color-bg-primary);
  }

  .app-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .app-main--with-sidebar {
    margin-left: var(--module-shell-width, 220px);
  }

  .app-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
  }

  .app-content--module {
    padding: 0;
    box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    .app-main--with-sidebar {
      margin-left: 0;
    }

    .app-content {
      padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
    }
  }
`;
